import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubmitReview() {
  const { t, i18n } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Antes: GET /bookings traía TODAS las reservas del cliente solo para
    // buscar una por id en el array. Con historial largo, esto descargaba
    // decenas de reservas innecesariamente en cada visita a esta página.
    fetch(`${BACKEND_URL}/api/bookings/${bookingId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(b => {
        if (!b) navigate('/client/dashboard');
        else setBooking(b);
      })
      .catch(() => navigate('/login'));
  }, [bookingId, navigate]);

  const handleSubmit = async () => {
    if (rating === 0) { setError(t('review.selectRating')); return; }
    if (comment.trim().length < 10) { setError(t('review.writeMin10')); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ booking_id: bookingId, rating, comment: comment.trim() })
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const d = await res.json();
        setError(d.detail || t('review.couldNotSendReview'));
      }
    } catch {
      setError(t('review.connectionError2'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#134168] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 max-w-md py-20 text-center">
        <div className="bg-white rounded-3xl p-10 shadow-sm">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('review.thanksForReview')}</h2>
          <p className="text-gray-500 mb-6">{t('review.reviewPendingNote')}</p>
          <button
            onClick={() => navigate('/client/dashboard')}
            className="bg-[#134168] text-white px-8 py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-all"
          >
            {t('review.backToMyBookings')}
          </button>
        </div>
      </div>
    </div>
  );

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';
  const ratingLabels = ['', t('review.rating1'), t('review.rating2'), t('review.rating3'), t('review.rating4'), t('review.rating5')];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 max-w-lg py-10">
        <button onClick={() => navigate('/client/dashboard')} className="mb-6 flex items-center gap-2 text-gray-500 hover:text-[#134168] font-medium transition-colors text-sm">
          ← {t('review.backToMyBookings')}
        </button>

        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold mb-1">{t('review.rateExperience')}</h1>
          <p className="text-gray-500 text-sm mb-6">{t('review.bookingLabel')} <span className="font-mono font-bold">{booking.booking_code}</span></p>

          <div className="flex gap-4 mb-6 p-4 bg-gray-50 rounded-2xl">
            <img
              src={booking.asset?.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
              alt={booking.asset?.name}
              className="w-20 h-16 rounded-xl object-cover shrink-0"
            />
            <div>
              <p className="font-bold">{booking.asset?.name || t('review.yourRental')}</p>
              <p className="text-sm text-gray-500">{new Date(booking.start_datetime).toLocaleDateString(locale)} · {booking.end_hours}h</p>
              <p className="text-sm font-bold text-[#134168]">{formatDOP(booking.total_price)}</p>
            </div>
          </div>

          <div className="mb-6">
            <p className="font-semibold mb-3">{t('review.howWouldYouRate')}</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onMouseEnter={() => setHovered(star)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setRating(star)}
                  aria-label={ratingLabels[star]}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 ${star <= (hovered || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'}`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-500 mt-2">{ratingLabels[rating]}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block font-semibold mb-2">{t('review.tellUsMore')} <span className="text-gray-400 font-normal">{t('review.minChars10')}</span></label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows="4"
              placeholder={t('review.reviewPlaceholder')}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{t('review.charsCount', { count: comment.length })}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#39b4aa] transition-all disabled:opacity-50"
          >
            {submitting ? t('review.sending') : t('review.submitReview')}
          </button>
        </div>
      </div>
    </div>
  );
}
